import { FC, useState } from 'react'

import { Box, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import { Reaction } from '@factorialco/f0-react/icons/app'
import {
  EmployeePulseWidgetQuestionDocument,
  EngagementEmployeePulseWidgetDocument,
  EngagementEmployeePulseWidgetQuery,
  EngagementWidgetCreateEmployeePulseDocument,
} from 'generated/resources/graphql'
import { keyBy } from 'lodash'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { EmployeePulseIcon } from 'modules/engagement/components/EmployeePulseIcon'
import { NewEmployeePulseModal } from 'modules/engagement/components/NewEmployeePulseModal'

import './index.styles.css'
const DAYS_TO_SHOW = 7
const DATE_FORMAT = 'YYYY-MM-DD'

const prepareData = (data: EngagementEmployeePulseWidgetQuery) => {
  const pulses = data.engagement.employeePulsesConnection.nodes

  // Use moment.utc() to correctly interpret UTC dates from backend and display on correct day
  return keyBy(pulses, (pulse) => moment.utc(pulse.submittedDate).format(DATE_FORMAT))
}

type PreparedData = ReturnType<typeof prepareData>

const EmployeePulseComponent: FC<{
  data: PreparedData
  employeeId: string
  onClickWidget?: () => void
}> = ({ data, employeeId }) => {
  const { employee } = useCurrentSession()
  const [selectedPulse, setSelectedPulse] = useState<string | null>(null)
  const existingSelectedPulse = selectedPulse ? data[selectedPulse] : undefined
  const isSelf = employeeId === employee?.id

  const [createEmployeePulse] = useMutation(EngagementWidgetCreateEmployeePulseDocument, {
    extractorFn: (mutationData) => mutationData.engagementMutations.createEngagementEmployeePulse,
  })

  return (
    <Widget
      header={{
        title: i18n.t('engagement.employee_pulse_widget.title'),
      }}
    >
      <Box flexDirection="row" justifyContent="spaceBetween" paddingTop="s8" paddingX="s8">
        {Array.from({ length: DAYS_TO_SHOW }).map((_, index) => {
          const currentDay = DAYS_TO_SHOW - 1 - index
          const date = moment().subtract(currentDay, 'days')
          const dateKey = date.format(DATE_FORMAT)
          const dateName = moment(dateKey).isSame(moment(), 'day')
            ? i18n.t('common.today')
            : moment(dateKey).format('dddd').charAt(0)
          const existingPulse = data[dateKey]

          if (!isSelf) {
            return (
              <DayWrapper key={dateKey} label={existingPulse?.comment} dateName={dateName}>
                <EmployeePulseIcon
                  hasComment={!!existingPulse?.comment}
                  mood={existingPulse?.value}
                />
              </DayWrapper>
            )
          }

          return (
            <DayWrapper key={dateKey} label={existingPulse?.comment} dateName={dateName}>
              {existingPulse ? (
                <Box
                  component="button"
                  onClick={() => setSelectedPulse(dateKey)}
                  className={'EmployeePulseWidget_button'}
                  aria-label={i18n.t('engagement.employee_pulse_widget.edit_pulse', {
                    date: dateName,
                  })}
                >
                  <EmployeePulseIcon
                    hasComment={!!existingPulse?.comment}
                    mood={existingPulse.value}
                  />
                </Box>
              ) : (
                <F0Button
                  label={i18n.t('engagement.employee_pulse_widget.fill_pulse', {
                    date: dateName,
                  })}
                  hideLabel
                  onClick={() => setSelectedPulse(dateKey)}
                  size="md"
                  variant="ghost"
                  icon={Reaction}
                />
              )}
            </DayWrapper>
          )
        })}

        {selectedPulse && (
          <NewEmployeePulseModal
            source="employee_profile"
            onClose={() => setSelectedPulse(null)}
            onSubmit={(formData) => {
              return createEmployeePulse({
                variables: {
                  ...formData,
                  date: selectedPulse,
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Send browser timezone to backend
                },
                refetchQueries: [
                  EngagementEmployeePulseWidgetDocument,
                  EmployeePulseWidgetQuestionDocument,
                ],
                awaitRefetchQueries: true,
                onCompleted(result) {
                  const isSuccess =
                    result?.engagementMutations.createEngagementEmployeePulse.employeePulse

                  if (isSuccess) {
                    track(trackingEvents.engagement.employeePulseResponseSubmitted, {
                      submission_method: 'profile_manual',
                      pulse_value: formData.value,
                      comment_provided: !!formData.comment,
                    })
                  }
                },
              })
            }}
            employeePulse={existingSelectedPulse}
          />
        )}
      </Box>
    </Widget>
  )
}

export const buildEmployeePulseWidget = () => {
  return buildWidget({
    id: 'employee-pulse',
    document: EngagementEmployeePulseWidgetDocument,
    isEmpty: (_data) => false,
    prepareData,
    component: EmployeePulseComponent,
    policies: [],
  })
}

function DayWrapper({
  children,
  label,
  dateName,
}: {
  children: React.ReactNode
  label?: string | null
  dateName: string
}) {
  return (
    <WithTooltip label={label}>
      <Box alignItems="center" gap="s4" cursor="default">
        {children}

        <Text.Normal size="200" weight="medium" color="grey700" capitalize>
          {dateName}
        </Text.Normal>
      </Box>
    </WithTooltip>
  )
}

function WithTooltip({ children, label }: { children: React.ReactNode; label?: string | null }) {
  const [isOpen, onToggle] = useToggle()

  if (!label) return <>{children}</>

  return (
    <Tooltip.Alt
      toggle={onToggle}
      open={isOpen}
      attachment="bottom center"
      label={
        <Text.Normal size="100" whiteSpace="breakSpaces">
          {label}
        </Text.Normal>
      }
    >
      {children}
    </Tooltip.Alt>
  )
}
